import React from "react";
import { Button } from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./DocumentTypeModal.css";

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10 9 9 9 8 9"></polyline>
  </svg>
);

const DocumentTypeModal = ({
  open,
  onClose,
  onViewRecords,
  title,
  documentType = "Medical Document",
  documentId = null,
  analysisIncomplete = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!open) return null;

  // ✅ Mapping des types de documents avec traductions
  const documentConfig = {
    "BILAN_BIOCHIMIQUE": {
      label: t('documentTypeModal.types.BILAN_BIOCHIMIQUE'),
      route: "/records/BILAN_BIOCHIMIQUE",
      collection: "bilan"
    },
    "IRM": {
      label: t('documentTypeModal.types.IRM'),
      route: "/records/IRM",
      collection: "irm"
    },
    "ECHOGRAPHIE": {
      label: t('documentTypeModal.types.ECHOGRAPHIE'),
      route: "/records/ECHOGRAPHIE",
      collection: "ecographie"
    },
    "RADIOGRAPHIE": {
      label: t('documentTypeModal.types.RADIOGRAPHIE'),
      route: "/records/RADIOGRAPHIE",
      collection: "rapport"
    },
    "ORDONNANCE": {
      label: t('documentTypeModal.types.ORDONNANCE'),
      route: "/records/ORDONNANCE",
      collection: "ordonnance"
    },
    "COMPTE_RENDU": {
      label: t('documentTypeModal.types.COMPTE_RENDU'),
      route: "/records/COMPTE_RENDU",
      collection: "compte_rendu"
    },
    "AUTRE": {
      label: t('documentTypeModal.types.AUTRE'),
      route: "/records/AUTRE",
      collection: "autre"
    },
  };

  const config = documentConfig[documentType] || {
    label: documentType,
    route: "/records/AUTRE",
    collection: "autre"
  };

  const handleViewClick = () => {
    if (onViewRecords) {
      onViewRecords(documentType, config.collection, documentId);
    } else {
      navigate(config.route, { 
        state: { 
          selectedDocumentId: documentId,
          documentType: documentType,
          autoSelect: true,
          fromUpload: true
        } 
      });
    }

    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container doc-type-modal">
        <button
          onClick={onClose}
          className="close-button"
          aria-label={t('documentTypeModal.close')}
        >
          <CloseIcon />
        </button>

        <div className="modal-content">
          <div className="success-icon-wrapper">
            {analysisIncomplete ? (
              <WarningOutlined style={{ fontSize: 64, color: "#faad14" }} />
            ) : (
              <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a" }} />
            )}
          </div>
   
          <h2 className="modal-title">
            {analysisIncomplete
              ? t("upload.analysisIncompleteTitle")
              : title || t("documentTypeModal.defaultTitle")}
          </h2>

          <div className="icon-wrapper">
            <div className="icon-circle">
              <div className="icon-background"></div>
              <div className="icon-container">
                <DocumentIcon />
              </div>
            </div>
          </div>

          <h3 className="step-title">{config.label}</h3>

          <p className="step-description">
            {analysisIncomplete
              ? t("upload.analysisIncompleteMessage")
              : t("documentTypeModal.successMessage")}
          </p>

          <div className="modal-actions">
            <Button size="large" onClick={onClose}>
              {t('documentTypeModal.close')}
            </Button>
            <Button type="primary" size="large" onClick={handleViewClick}>
              {t('documentTypeModal.viewButton', { type: config.label })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentTypeModal;
